import { Link } from "react-router-dom";
import { Swords, Crown, TrendingUp, Flame } from "lucide-react";
import { RankedTierChip } from "../pages/Ranked";
import { RankBadge } from "./RankBadge";

const TIER_GRADIENT = {
  iron:        "from-zinc-700 to-zinc-900",
  bronze:      "from-amber-700 to-amber-900",
  silver:      "from-slate-400 to-slate-700",
  gold:        "from-yellow-500 to-amber-700",
  platinum:    "from-teal-400 to-teal-700",
  diamond:     "from-cyan-300 to-blue-700",
  master:      "from-fuchsia-500 to-purple-800",
  grandmaster: "from-rose-500 to-red-800",
};

function ModeCard({ label, block }) {
  const tier = block?.tier || "iron";
  const grad = TIER_GRADIENT[tier] || TIER_GRADIENT.iron;
  const gp = block?.games_played || 0;
  const wins = block?.wins || 0;
  const winRate = gp ? Math.round((wins / gp) * 100) : 0;

  if (gp === 0) {
    return (
      <div
        data-testid={`ranked-card-${label.toLowerCase().replace(/\s/g, "-")}-empty`}
        className="rounded-2xl border border-white/10 bg-[var(--surface-2)]/40 p-5 text-center space-y-3"
      >
        <Swords className="mx-auto text-zinc-600" size={24} />
        <div className="text-sm font-bold text-zinc-300">{label}</div>
        <div className="text-xs text-zinc-500">Not yet ranked — play your first match</div>
        <Link
          to="/ranked"
          className="inline-block text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-full bg-[var(--accent)] text-black font-bold"
        >
          Open ranked →
        </Link>
      </div>
    );
  }

  return (
    <div
      data-testid={`ranked-card-${label.toLowerCase().replace(/\s/g, "-")}`}
      className={`relative overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br ${grad} bg-opacity-15 p-5 space-y-4`}
      style={{ backgroundBlendMode: "multiply" }}
    >
      <div className="absolute inset-0 bg-black/55 pointer-events-none" />
      <div className="relative">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-3">
            <RankBadge tier={tier} size="lg" animated showLabel={false} />
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-300/90">{label}</div>
              <div className="mt-1 text-2xl font-black tabular-nums" data-testid="ranked-card-mmr">
                {block.mmr} <span className="text-xs font-bold tracking-widest uppercase text-zinc-300/80">MMR</span>
              </div>
            </div>
          </div>
          <RankedTierChip tier={tier} />
        </div>
        <div className="mt-4 grid grid-cols-2 gap-2 text-xs">
          <div className="rounded-lg bg-black/40 p-2">
            <div className="text-[9px] uppercase tracking-widest text-zinc-500 flex items-center gap-1"><Crown size={9} /> Peak</div>
            <div className="font-bold tabular-nums">{block.peak_mmr}</div>
          </div>
          <div className="rounded-lg bg-black/40 p-2">
            <div className="text-[9px] uppercase tracking-widest text-zinc-500">Games</div>
            <div className="font-bold tabular-nums">{gp}</div>
          </div>
          <div className="rounded-lg bg-black/40 p-2">
            <div className="text-[9px] uppercase tracking-widest text-zinc-500">Wins</div>
            <div className="font-bold tabular-nums">{wins} · {winRate}%</div>
          </div>
          <div className="rounded-lg bg-black/40 p-2">
            <div className="text-[9px] uppercase tracking-widest text-zinc-500 flex items-center gap-1"><TrendingUp size={9} /> Top</div>
            <div className="font-bold tabular-nums">{block.top_finishes || 0}</div>
          </div>
          <div className="rounded-lg bg-black/40 p-2 col-span-2">
            <div className="text-[9px] uppercase tracking-widest text-zinc-500 flex items-center gap-1"><Flame size={9} /> Streaks</div>
            <div className="font-bold tabular-nums">curr {block.current_streak || 0} · best {block.best_streak || 0}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function RankedStatsCard({ ranked }) {
  const cls = ranked?.classic;
  const cp = ranked?.career_path;
  const qz = ranked?.quiz;
  return (
    <section data-testid="ranked-section" className="space-y-3">
      <div className="flex items-center gap-2">
        <Swords size={14} className="text-emerald-400" />
        <h2 className="text-lg font-bold uppercase tracking-widest">Ranked</h2>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        <ModeCard label="Classic" block={cls} />
        <ModeCard label="Career Path" block={cp} />
        <ModeCard label="Quiz" block={qz} />
      </div>
    </section>
  );
}
